import { REHYDRATE } from "redux-persist";
import { ProductListItem, ProductsListFilters, ProductsListType } from "../../Actions/ProductListActions";
import * as types from "./../../Types/ProductListTypes";

export const initialState: ProductsListType = {
    isFetchingProducts: false,
    products: [],
    filter: {
        filterMenuOpen: false,
        filterMenuToggledByUser: false,
        brandIds: [],
        categoryIds: [],
        // Blank means no explicit sort - GetProductsList omits it from the request so the
        // backend's own ProductSort.Default (legacy order) applies.
        sort: "",
        inStockFilter: false,
        outOfStockFilter: false,
        uomsFilter: [],
    },
    brands: [],
    brandsFetched: false,
    categories: [],
    isFetchingCategories: false,
    categoriesFetched: false,
    categoryRouteObjects: [],
    numberOfCategories: null,
    message: "",
    productListViewStyle: "gallery-view",
    serviceHasMore: true,
};

export const ProductListReducer = (
    state = initialState,
    action: { type: string; payload: any | undefined }
): ProductsListType => {
    switch (action.type) {
        case REHYDRATE: {
            // autoMergeLevel2 replaces the nested `filter` wholesale instead of deep-merging, so a
            // blob persisted before `sort` existed would rehydrate `filter` without it. Returning a
            // new slice makes autoMergeLevel2 defer to us, so we redo its level-1 merge and backfill
            // missing filter fields from initialState, keeping the user's saved selections.
            const incoming = action.payload && action.payload.productListReducer;
            if (!incoming) {
                return state;
            }
            return {
                ...state,
                ...incoming,
                filter: {
                    ...initialState.filter,
                    ...incoming.filter,
                },
            };
        }
        case types.Fetching_Products_List:
            return {
                ...state,
                isFetchingProducts: true,
                message: "",
            };
        case types.Fetch_Products_List:
            const serviceHasMore = Array.from(action.payload.products).length > 0;

            if (serviceHasMore) {
                action.payload.products.forEach((item: ProductListItem) => {
                    if (!state.products.some((product: { id: number }) => product.id === item.id)) {
                        state.products = [...state.products, item];
                    }
                });
            }
            return {
                ...state,
                isFetchingProducts: false,
                products: state.products,
                message: "",
                serviceHasMore: serviceHasMore,
            };
        case types.Fetch_Products_Error:
            return {
                ...state,
                isFetchingProducts: false,
                message: action.payload.message,
            };
        case types.Product_List_View_List:
            return {
                ...state,
                productListViewStyle: "list-view",
            };
        case types.Product_List_View_Gallery:
            return {
                ...state,
                productListViewStyle: "gallery-view",
            };
        case types.Reset_Products_List:
            return {
                ...state,
                isFetchingProducts: false,
                products: [],
                message: "",
                serviceHasMore: initialState.serviceHasMore,
            };
        case types.Fetch_Brands_List:
            return {
                ...state,
                brands: action.payload.brands,
                brandsFetched: action.payload.brandsFetched,
            };
        case types.Reset_Brands_List:
            return {
                ...state,
                brands: [],
                brandsFetched: initialState.brandsFetched,
            };
        case types.Update_Products_Sort:
            return {
                ...state,
                filter: {
                    ...state.filter,
                    sort: action.payload.sort,
                },
            };
        case types.Filter_Menu_Toggle_Status:
            // Computed from state.filter.filterMenuOpen without mutating it: the old filter
            // object is still referenced as "previous props" by connected components until this
            // dispatch's re-render completes, so mutating it in place corrupted that comparison
            // (both "previous" and "next" would read the post-toggle value).
            return {
                ...state,
                filter: {
                    ...state.filter,
                    filterMenuOpen: !state.filter.filterMenuOpen,
                    filterMenuToggledByUser: action.payload.filterMenuToggledByUser,
                },
            };
        case types.Reset_Products_Filter:
            // Clear All resets every filter section but preserves the active sort order.
            return {
                ...state,
                filter: {
                    ...state.filter,
                    filterMenuOpen: initialState.filter.filterMenuOpen,
                    brandIds: initialState.filter.brandIds,
                    categoryIds: initialState.filter.categoryIds,
                    inStockFilter: initialState.filter.inStockFilter,
                    outOfStockFilter: initialState.filter.outOfStockFilter,
                    uomsFilter: initialState.filter.uomsFilter,
                    filterMenuToggledByUser: state.filter.filterMenuToggledByUser,
                },
            };
        case types.Update_Products_Filter:
            const updatedFields: ProductsListFilters = {};

            // Compare against undefined rather than testing truthiness, so unchecking a box
            // (false) and clearing a list ([]) both apply instead of being read as "not supplied".
            if (action.payload.brandIds !== undefined) updatedFields.brandIds = action.payload.brandIds;
            if (action.payload.categoryIds !== undefined) updatedFields.categoryIds = action.payload.categoryIds;
            if (action.payload.inStockFilter !== undefined) updatedFields.inStockFilter = action.payload.inStockFilter;
            if (action.payload.outOfStockFilter !== undefined)
                updatedFields.outOfStockFilter = action.payload.outOfStockFilter;
            if (action.payload.uomsFilter !== undefined) updatedFields.uomsFilter = action.payload.uomsFilter;

            return {
                ...state,
                filter: {
                    ...state.filter,
                    ...updatedFields,
                },
            };
        case types.Fetch_Categories_List:
            return {
                ...state,
                categories: action.payload.categories,
                isFetchingCategories: action.payload.isFetchingCategories,
                categoriesFetched: action.payload.categoriesFetched,
                numberOfCategories: action.payload.numberOfCategories,
            };
        case types.Fetch_Categories_Error:
            return {
                ...state,
                message: action.payload.message,
                isFetchingCategories: false,
                categoriesFetched: true,
            };
        case types.Reset_Categories_List:
            return {
                ...state,
                categories: [],
                categoriesFetched: initialState.categoriesFetched,
                numberOfCategories: initialState.numberOfCategories,
            };
        case types.Reset_Category_Route_Objects:
            return {
                ...state,
                categoryRouteObjects: [],
            };
        case types.Create_Category_Route_Objects:
            return {
                ...state,
                categoryRouteObjects: action.payload.routes,
            };
        default:
            return state;
    }
};
